import { faChevronsRight, faRobot, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { zodResolver } from "@hookform/resolvers/zod";

import { TextField } from "@mui/material";

import { useForm, Controller } from "react-hook-form";

import { Button } from "@/components/Button/Button";
import { Icon, PermissionCheck } from "@/components";
import { tasksSelector } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";

import { ERROR_TYPE, translateError } from "@/utils";

import DeleteModal from "@/components/Modals/DeleteModal";

import { ButtonColor } from "@/components/Button/types";

import { PermissionRoles } from "@/enum";

import { useGetCommentsByTaskQuery } from "@/pages/Private/redux/tasks-and-subtasks/task-and-subtasks-activity.api";

import { ToDoStatusDropDown } from "./ToDoStatusDropdown";
import {
	UpdateTaskInfo,
	UpdateTaskInfoSchema,
} from "../../../TasksAndSubTasks/schema/tasks-and-subtasks";
import ActivityTabs from "../../../TasksAndSubTasks/components/ActivityTabs";
import DetailsMeta from "../../../TasksAndSubTasks/components/DetailsMeta";
import SubTaskList from "../../../TasksAndSubTasks/components/SubTaskList";
import UnsavedChangesModal from "../../../TasksAndSubTasks/components/UnsavedChangesModal";
import AssigneeSelect from "../../../TasksAndSubTasks/components/fields/AssigneeSelect";
import TagSelect from "../../../TasksAndSubTasks/components/fields/TagSelect";
import { useUnsavedGuard } from "../../../TasksAndSubTasks/useUnsavedGuard";
import { useToDoSocket } from "../providers/SocketContext";

const AiChat = lazy(() => import("../../../TasksAndSubTasks/components/AiChat"));

interface ToDoDetailsProps {
	companyToDoProgressId: number | null;
	close: (task: null) => void;
	showSubTaskDetails?: (subTaskId: number) => void;
}

/** The rail's field labels — small, upper-case, quiet, like the reference layout. */
const railLabel = "text-[12px] font-bold uppercase tracking-wide leading-4 text-subtle";

export default function ToDoDetails({
	close,
	companyToDoProgressId,
	showSubTaskDetails,
}: ToDoDetailsProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const tasks = useSelector(tasksSelector);
	const data = useMemo(
		() =>
			companyToDoProgressId ? tasks.find((item) => item.id === companyToDoProgressId) : undefined,
		[companyToDoProgressId, tasks]
	);

	const dataRef = useRef(data);

	useGetCommentsByTaskQuery(data?.id ?? 0, {
		skip: !data?.id,
	});

	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	const { updateTask, updateSubTask, deleteTask } = useToDoSocket();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isDirty },
	} = useForm<UpdateTaskInfo>({
		defaultValues: {
			id: data?.id ?? -1,
			title: data?.title,
			description: data?.description,
			complexityEstimate: data?.complexityEstimate,
			status: data?.status,
			priority: data?.priority,
			tagId: data?.tagId ?? null,
			assigneeId: data?.assigneeId,
		},
		resolver: zodResolver(UpdateTaskInfoSchema),
	});

	useEffect(() => {
		if (data) {
			reset({
				id: data.id,
				title: data.title,
				description: data.description,
				complexityEstimate: data.complexityEstimate,
				status: data.status,
				priority: data.priority,
				tagId: data.tagId,
				assigneeId: data.assigneeId,
			});
		}
	}, [
		data?.id,
		data?.assigneeId,
		data?.title,
		data?.description,
		data?.status,
		data?.priority,
		reset,
		data?.tagId,
	]);

	const onSubmit = useCallback(
		(formValues: UpdateTaskInfo) => {
			const current = dataRef.current;

			if (!current) {
				return;
			}

			const merged: UpdateTaskInfo = {
				...formValues,
				id: current.id ?? -1,
				status: current.status ?? formValues.status,
			};

			updateTask(merged.id, merged);

			reset(merged);
		},
		[updateTask, updateSubTask, reset, data?.subTasks]
	);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isAiChatOpen, setIsAiChatOpen] = useState(false);

	const handleSave = useCallback(async () => {
		await handleSubmit(onSubmit)();
	}, [handleSubmit, onSubmit]);

	const { guard, isPrompting, keepEditing, discard, saveAndContinue } = useUnsavedGuard({
		isDirty,
		reset,
		save: handleSave,
	});

	/** Every route out of the drawer goes through the guard, so no edit is dropped silently. */
	const requestClose = useCallback(() => guard(() => close(null)), [guard, close]);

	const openSubTask = useCallback(
		(subTaskId: number) =>
			guard(() => {
				close(null);
				showSubTaskDetails?.(subTaskId);
			}),
		[guard, close, showSubTaskDetails]
	);

	useEffect(() => {
		if (!data?.id) {
			return;
		}

		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "Escape") {
				return;
			}

			if (e.defaultPrevented) {
				return;
			}

			if (isDeleteModalOpen || isAiChatOpen || isPrompting) {
				return;
			}

			requestClose();
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [data?.id, isDeleteModalOpen, isAiChatOpen, isPrompting, requestClose]);

	return (
		<>
			<div
				aria-hidden
				className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ease-in-out
					${data ? "opacity-100" : "opacity-0 pointer-events-none"}`}
				onClick={requestClose}
			/>
			<div
				className={`fixed right-0 top-0 z-50 flex h-[100vh] w-[min(880px,96vw)] flex-col bg-white shadow-lg overflow-y-auto
					transition-all duration-300 ease-in-out
					${data ? "translate-x-0 opacity-100" : "translate-x-full opacity-0 pointer-events-none"}`}
			>
				{!!data?.id && (
					<>
						<div className="sticky top-0 z-10 w-full bg-white px-6 py-4 border-b border-border flex items-center justify-between gap-4">
							<Button
								className="rounded-[12px] bg-white !min-w-[44px] !min-h-[44px] !max-w-[44px] !max-h-[44px] !p-0 !shadow-none hover:bg-primary-gray-lightest"
								image={<Icon className="text-primary-blue-dark w-5 h-5" icon={faChevronsRight} />}
								title=""
								onClick={requestClose}
							/>
							<div className="flex items-center gap-3">
								{isDirty ? (
									<span className="text-[12px] leading-4 text-accent-strong">
										{ts("unsaved.badge")}
									</span>
								) : null}
								<Button
									className="rounded-[12px] bg-white !min-w-fit !min-h-[44px] !max-w-fit !py-2 !px-4 !border-[0.28px] !border-border hover:bg-primary-gray-lightest"
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="text-accent-strong w-5 h-5 mr-2" icon={faRobot} />}
									title={t("aiChat.toggle", "AI Assistant")}
									onClick={() => setIsAiChatOpen((v) => !v)}
								/>
								<Button
									className="rounded-[12px] !min-w-fit !min-h-[44px] !max-w-fit !py-2 !px-4"
									color={ButtonColor.ACTION}
									disabled={!isDirty}
									title={t("createTask.save")}
									onClick={handleSave}
								/>
							</div>
						</div>

						{/* grow/shrink-0: fills the drawer when the task is short so the rail
						    reaches the bottom, and still grows past it when it is long. */}
						<div className="grid grow shrink-0 basis-auto grid-cols-1 sm:grid-cols-[minmax(0,1fr)_300px] items-stretch">
							<div className="p-6 min-w-0 flex flex-col gap-6">
								<div className="w-full flex flex-col gap-2">
									<p className={railLabel}>{t("createTask.title")}</p>
									<Controller
										control={control}
										name="title"
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												error={!!errors?.title}
												helperText={
													!!errors?.title?.message
														? translateError[errors?.title?.message as unknown as ERROR_TYPE]
														: errors?.title?.message
												}
												inputProps={{
													style: { padding: "8px", fontSize: "18px", fontWeight: 600 },
												}}
												size="small"
											/>
										)}
									/>
								</div>

								<div className="w-full flex flex-col gap-2">
									<p className={railLabel}>{t("createTask.description")}</p>
									<Controller
										control={control}
										name="description"
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												multiline
												maxRows={10}
												minRows={5}
												size="small"
											/>
										)}
									/>
								</div>

								<SubTaskList
									subTasks={data.subTasks ?? []}
									task={data}
									onOpenSubTask={openSubTask}
								/>

								<ActivityTabs isTask companyToDoProgressId={companyToDoProgressId} />
							</div>

							<aside className="p-6 flex flex-col gap-6 border-t sm:border-t-0 sm:border-l border-border bg-neutral-25">
								<ToDoStatusDropDown companyToDoProgressId={data.id} isTask={data?.isTask} />

								<div className="w-full flex flex-col gap-1.5">
									<p className={railLabel}>{t("createTask.assignee")}</p>
									<Controller
										control={control}
										name="assigneeId"
										render={({ field }) => (
											<AssigneeSelect value={field.value} onChange={field.onChange} />
										)}
									/>
								</div>

								<div className="w-full flex flex-col gap-1.5">
									<p className={railLabel}>{t("createTask.tags")}</p>
									<Controller
										control={control}
										name="tagId"
										render={({ field }) => (
											<TagSelect value={field.value} onChange={field.onChange} />
										)}
									/>
								</div>

								<DetailsMeta createdAt={data.createdAt} updatedAt={data.updatedAt} />

								<PermissionCheck requiredPermissions={[PermissionRoles.ADMIN]}>
									<div className="mt-auto pt-4 border-t border-border">
										<Button
											className="rounded-[12px] w-full !min-h-[44px] !py-2 !px-4"
											color={ButtonColor.DANGER_SECONDARY}
											image={<Icon className="w-5 h-5 mr-2" icon={faTrashCan} />}
											title={t("createTask.deleteTask")}
											onClick={() => {
												if (data?.id) {
													setIsDeleteModalOpen(true);
												}
											}}
										/>
									</div>
								</PermissionCheck>
							</aside>
						</div>

						<Suspense fallback={null}>
							<AiChat
								isOpen={isAiChatOpen}
								taskId={data.id}
								onClose={() => setIsAiChatOpen(false)}
							/>
						</Suspense>
					</>
				)}
				<DeleteModal
					description={t("manage-to-dos.delete-description", { taskTitle: data?.title })}
					handleClose={() => setIsDeleteModalOpen(false)}
					handleDelete={() => {
						if (data && data.id) {
							deleteTask(data.id);
							setIsDeleteModalOpen(false);
						}
					}}
					isShowing={isDeleteModalOpen}
					title={t("manage-to-dos.delete-title")}
				/>
				<UnsavedChangesModal
					isOpen={isPrompting}
					onDiscard={discard}
					onKeepEditing={keepEditing}
					onSave={saveAndContinue}
				/>
			</div>
		</>
	);
}
